import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { ExitToApp } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import AppBar from "components/app-bar";
import SlideUpTransition from "components/slide-up-transition";
import useIdResource from "hooks/use-id-resource";
import {
  ChangeEvent,
  FormEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { account, auth, RootState } from "store";
import { StoreError } from "store/reducer";
import firebase from "store/firebase";
import {
  AccountProfile,
  accountSelectors,
} from "store/reducers/account/reducer";
import {
  CancelButton,
  Container,
  FeedbackStack,
  Form,
  Heading,
  InnerContainer,
  LogoutButton,
  LogoutHint,
  LogoutSection,
  SaveButton,
  Subheading,
} from "./Account.styles";

const getErrorMessage = (error: unknown): string => {
  if (error instanceof StoreError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
};

const Account = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const authEmail = useSelector<RootState, string | null>(
    (s) => s.auth.user?.email ?? null
  );
  const email = authEmail ?? firebase.auth().currentUser?.email ?? "";

  // Profile load goes through the id-resource thunk so the screen recovers
  // from the same A → B → A race that broke the rest of the app — and a
  // navigate-away-and-back triggers a fresh fetch instead of staring at a
  // local `useState(true)` that never resolves.
  const {
    item: profile,
    error: loadStoreError,
    showLoading: profileLoading,
  } = useIdResource<AccountProfile>({
    id: email,
    selectors: accountSelectors,
    load: useCallback((id: string) => account.load(id), []),
  });
  const loadError = loadStoreError ? loadStoreError.message : null;

  const saving = useSelector<RootState, boolean>(
    (s) => s.account.save.saving
  );
  const saved = useSelector<RootState, AccountProfile | null>(
    (s) => s.account.save.saved
  );
  const saveStoreError = useSelector<RootState, StoreError | null>(
    (s) => s.account.save.error
  );
  const saveError = saveStoreError ? getErrorMessage(saveStoreError) : null;

  const [displayName, setDisplayName] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [open, setOpen] = useState(true);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  // Sync the local edit field with the loaded profile. The `saved` flag also
  // updates this so the field reflects whatever the server actually accepted.
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName ?? "");
    }
  }, [profile]);

  useEffect(() => {
    if (saved) {
      setDisplayName(saved.displayName ?? "");
      setSaveSuccess(true);
      // Reset the slice's transient save state so navigating back doesn't
      // flash the old success / error from a previous edit.
      dispatch(account.resetSave());
    }
  }, [saved, dispatch]);

  // Clear any stale success / save error left over from a previous mount.
  useEffect(() => {
    return () => {
      dispatch(account.resetSave());
    };
  }, [dispatch]);

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDisplayNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDisplayName(event.target.value);
    if (saveError) {
      dispatch(account.resetSave());
    }
    setSaveSuccess(false);
  };

  const handleSave = () => {
    if (!email) return;
    setSaveSuccess(false);
    dispatch(account.save({ email, displayName }));
  };

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    handleSave();
  };

  const handleLogoutClick = () => {
    setLogoutConfirmOpen(true);
  };

  const handleCancelLogout = () => {
    setLogoutConfirmOpen(false);
  };

  const handleConfirmLogout = () => {
    setLogoutConfirmOpen(false);
    dispatch(auth.logout());
  };

  const handleExit = () => {
    history.push("/");
  };

  const noEmailError = !email ? "No current user." : null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleCancel}
      TransitionProps={{ onExited: handleExit }}
      TransitionComponent={SlideUpTransition}
    >
      <Container data-testid="account">
        <AppBar
          renderLeftComponent={() => (
            <CancelButton onClick={handleCancel} />
          )}
          title="Account"
          renderRightComponent={() => (
            <SaveButton
              onClick={handleSave}
              disabled={profileLoading || saving}
            />
          )}
        />
        <InnerContainer>
          {profileLoading ? (
            <CircularProgress />
          ) : (
            <Form id="account-form" onSubmit={handleFormSubmit}>
              <div>
                <Heading>Account</Heading>
                <Subheading>
                  Update your display name or sign out here.
                </Subheading>
              </div>

              <FeedbackStack>
                {noEmailError && <Alert severity="error">{noEmailError}</Alert>}
                {loadError && <Alert severity="error">{loadError}</Alert>}
                {saveError && <Alert severity="error">{saveError}</Alert>}
                {saveSuccess && <Alert severity="success">Account saved.</Alert>}
              </FeedbackStack>

              <TextField
                id="account-email"
                label="Email"
                value={email}
                variant="outlined"
                InputProps={{ readOnly: true }}
              />
              <TextField
                id="account-display-name"
                label="Display Name"
                value={displayName}
                onChange={handleDisplayNameChange}
                variant="outlined"
                helperText="Leave blank to fall back to your email."
                disabled={saving}
              />

              <LogoutSection>
                <LogoutButton
                  type="button"
                  variant="contained"
                  onClick={handleLogoutClick}
                  disabled={saving}
                  startIcon={<ExitToApp />}
                >
                  Logout
                </LogoutButton>
                <LogoutHint>You'll be asked to log in next time.</LogoutHint>
              </LogoutSection>
            </Form>
          )}
        </InnerContainer>
        <Dialog
          open={logoutConfirmOpen}
          onClose={handleCancelLogout}
          aria-labelledby="logout-confirm-title"
        >
          <DialogTitle id="logout-confirm-title">Log out?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You are about to leave the app and return to the login screen.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLogout} color="default">
              Stay
            </Button>
            <Button onClick={handleConfirmLogout} color="secondary" variant="contained">
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Dialog>
  );
};

export default Account;
